/**
 * Shared formatting and display helpers.
 * Used by Home, Events, Order, Payment, etc.
 */

export function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    THB: "฿",
    RUB: "₽",
    USD: "$",
    EUR: "€",
  };
  return symbols[currency] ?? currency;
}

export interface MinPriceResult {
  price: number | null;
  currency: string;
}

/** Event with ticketTypes that have price (and optionally currency). */
export interface EventWithPrices {
  ticketTypes: Array<{
    price: string | number;
    currency?: string | null;
  }>;
}

/**
 * Get minimum price from event's ticket types.
 * Always returns THB as the currency (primary display currency).
 * Finds the minimum price among THB-priced tickets, or falls back to first ticket type.
 *
 * This ensures consistent display: THB is always shown as the primary currency,
 * and currency conversion only happens on payment page when user explicitly selects RUB.
 */
export function getMinPrice(event: EventWithPrices): MinPriceResult {
  if (event.ticketTypes.length === 0) {
    return { price: null, currency: "THB" };
  }

  // Filter ticket types that have THB currency (or no currency specified = THB default)
  const thbTickets = event.ticketTypes.filter(
    (t) => !t.currency || t.currency === "THB"
  );

  // Use THB tickets if available, otherwise fall back to all tickets
  const ticketsToUse = thbTickets.length > 0 ? thbTickets : event.ticketTypes;

  const prices = ticketsToUse.map((t) => parseFloat(String(t.price)));
  const minPrice = Math.min(...prices);

  // Always return THB as the display currency
  return { price: minPrice, currency: "THB" };
}

/** Format event start for list display (short). */
export function formatEventDateShort(startsAt: string): string {
  return new Date(startsAt).toLocaleDateString("ru-RU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Format event start for detail (long). */
export function formatEventDateLong(startsAt: string): string {
  return new Date(startsAt).toLocaleDateString("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format relative time (e.g., "2 мин", "1 час", "вчера").
 * Used for live feeds and activity timestamps.
 */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return "сейчас";
  }
  if (diffMin < 60) {
    return `${diffMin} мин`;
  }
  if (diffHour < 24) {
    return `${diffHour} ч`;
  }
  if (diffDay === 1) {
    return "вчера";
  }
  if (diffDay < 7) {
    return `${diffDay} дн`;
  }
  // Fallback to short date
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}
